import { useCallback, useEffect, useRef, useState } from 'react';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { useContextualComments } from './useContextualComments';
import { DEAL_COMMENTS_NR_ACTIONS, SHOW_DEAL_MESSAGES_KEY } from './dealCommentsConstants';
import type { CommentEvent, UseDealCommentsPanelParams, UseDealCommentsPanelResult } from './types';

export function useDealCommentsPanel({
    isEnabled,
    referenceId,
    commonOrgId
}: UseDealCommentsPanelParams): UseDealCommentsPanelResult {
    const { ready: isCommentsScriptReady } = useContextualComments(isEnabled);
    const [isOpen, setIsOpen] = useState(() => offerSessionUtils.getSessionValue(SHOW_DEAL_MESSAGES_KEY) === 'true');
    const [showRedDot, setShowRedDot] = useState(false);
    const missingIdentityTracked = useRef(false);
    const commentsElementRef = useRef<HTMLElement>(null);

    // showDealMessages is a one-shot "start the app with comments visible" signal carried on a
    // link's query string — not a standing preference. Clear it right after consuming it so it
    // can never re-open the panel later in the same tab (a different deal, a template apply, a
    // scenario hydrate) — those aren't supposed to be side effects of a link someone followed once.
    useEffect(() => {
        offerSessionUtils.deleteSessionValue(SHOW_DEAL_MESSAGES_KEY);
    }, []);

    useEffect(() => {
        // Gated on script readiness, not just isEnabled — the web component (and thus the DOM node
        // this listens on) is only mounted by ContextualCommentsPanelContent once the script has
        // finished its async load, so this must re-run when that flips true, not just on mount.
        if (!isEnabled || !isCommentsScriptReady) return;

        const handleCommentsUpdated: EventListener = (event) => {
            const { detail } = event as CommentEvent;
            if (typeof newrelic === 'object') {
                newrelic.addPageAction(DEAL_COMMENTS_NR_ACTIONS.updated, {
                    count: detail.count,
                    namespace: detail.commentStream?.namespace,
                    referenceId: detail.commentStream?.referenceId
                });
            }
            if (detail.count > 0) {
                setShowRedDot(true);
            }
        };

        // Scoped to this instance's own element via commentsElementRef rather than a global
        // document.querySelector — more than one contextual-comments widget can be mounted at
        // once (e.g. the persistent header instance plus the deal-lifecycle drawer's own), and a
        // global query can't tell which one belongs to this hook instance.
        const commentElement = commentsElementRef.current;
        commentElement?.addEventListener('commentsUpdated', handleCommentsUpdated);

        return () => {
            commentElement?.removeEventListener('commentsUpdated', handleCommentsUpdated);
        };
    }, [isEnabled, isCommentsScriptReady]);

    // Track missing deal identity once — if the panel renders without a resolvable referenceId or
    // commonOrgId, it silently shows the wrong (or an empty) comment thread. Fire a NR page action
    // so this is detectable without a user report.
    if (isCommentsScriptReady && !missingIdentityTracked.current && (!referenceId || !commonOrgId)) {
        missingIdentityTracked.current = true;
        if (typeof newrelic === 'object') {
            newrelic.addPageAction(DEAL_COMMENTS_NR_ACTIONS.missingIdentity, {
                hasDealXgId: String(!!referenceId),
                hasCommonOrgId: String(!!commonOrgId)
            });
        }
    }

    const open = useCallback(() => {
        setIsOpen(true);
        setShowRedDot(false);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
        setShowRedDot(false);
    }, []);

    const toggle = useCallback(() => {
        setIsOpen((prev) => !prev);
        setShowRedDot(false);
    }, []);

    return { isOpen, showRedDot: showRedDot && !isOpen, open, close, toggle, commentsElementRef };
}
